import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Pencil, Trash2, Search, Download, Wallet, FileText, CheckCircle2, Clock, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  first_name: '', last_name: '', address: '', phone: '', email: '',
  seller_code: '', status: 'active',
  commission_amount_chf: 0,
  iban: '', bic: '', bank_account_holder: '',
};

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const fmtChf = (v) => `CHF ${Number(v || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('fr-CH'); } catch { return iso; }
};

const SellersPage = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  // Commissions modal state
  const [commissionsOpen, setCommissionsOpen] = useState(false);
  const [commissionsSeller, setCommissionsSeller] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchSellers = async () => {
    setLoading(true);
    try {
      const url = new URL(`${API}/admin/sellers`);
      if (search) url.searchParams.set('search', search);
      if (statusFilter) url.searchParams.set('status', statusFilter);
      const r = await fetch(url, { headers });
      const data = await r.json();
      setSellers(data.sellers || []);
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSellers(); /* eslint-disable-next-line */ }, [statusFilter]);

  const handleOpenNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const handleOpenEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editing ? `${API}/admin/sellers/${editing.id}` : `${API}/admin/sellers`;
      const method = editing ? 'PATCH' : 'POST';
      const body = { ...form };
      if (!body.email) delete body.email;
      if (!body.seller_code) delete body.seller_code;
      // Normalise commission to number
      body.commission_amount_chf = Number(body.commission_amount_chf || 0);
      // Clean empties for optional fields so backend treats them as None
      ['iban', 'bic', 'bank_account_holder', 'address', 'phone'].forEach((k) => {
        if (body[k] === '') body[k] = null;
      });
      const r = await fetch(url, { method, headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.detail || 'Erreur');
      }
      toast({ title: editing ? 'Vendeur mis à jour' : 'Vendeur créé', description: 'Modifications enregistrées.' });
      setOpen(false);
      fetchSellers();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Supprimer le vendeur ${s.first_name} ${s.last_name} ?`)) return;
    try {
      const r = await fetch(`${API}/admin/sellers/${s.id}`, { method: 'DELETE', headers });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.detail || 'Erreur');
      }
      toast({ title: 'Vendeur supprimé' });
      fetchSellers();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.message });
    }
  };

  const handleExport = async (s) => {
    try {
      const r = await fetch(`${API}/admin/sellers/${s.id}/customers/export`, { headers });
      if (!r.ok) throw new Error('Export échoué');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `vendeur-${s.seller_code}-clients.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.message });
    }
  };

  const openCommissions = (s) => {
    setCommissionsSeller(s);
    setCommissionsOpen(true);
  };

  const exportAllCommissionsCsv = async () => {
    try {
      const r = await fetch(`${API}/admin/commissions/export.csv`, { headers });
      if (!r.ok) throw new Error('Export échoué');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'commissions.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.message });
    }
  };

  const reconcileCommissions = async () => {
    try {
      const r = await fetch(`${API}/admin/commissions/reconcile`, { method: 'POST', headers });
      const data = await r.json();
      toast({
        title: 'Rattrapage terminé',
        description: `${data.invoice_created + data.stripe_created} nouvelles commissions créées (${data.skipped_already_existing} déjà présentes).`,
      });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.message });
    }
  };

  const filtered = sellers.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.first_name?.toLowerCase().includes(q) ||
      s.last_name?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q) ||
      (s.seller_code || '').includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vendeurs</h1>
          <p className="text-gray-600 mt-1">Partenaires vendeurs, codes & commissionnement.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={reconcileCommissions} data-testid="btn-reconcile-commissions" title="Recalcule les commissions des contrats déjà payés">
            <Wallet size={14} className="mr-1" /> Rattraper commissions
          </Button>
          <Button variant="outline" onClick={exportAllCommissionsCsv} data-testid="btn-export-all-commissions">
            <Download size={14} className="mr-1" /> Export CSV commissions
          </Button>
          <Button onClick={handleOpenNew} className="bg-green-600 hover:bg-green-700" data-testid="btn-new-seller">
            <Plus size={16} className="mr-1" /> Nouveau vendeur
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Liste des vendeurs</CardTitle>
          <CardDescription>{filtered.length} vendeur{filtered.length > 1 ? 's' : ''}</CardDescription>
          <div className="flex flex-wrap gap-3 pt-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher (nom, email, code)…"
                className="pl-9"
                data-testid="input-search-seller"
              />
            </div>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-44" data-testid="filter-seller-status"><SelectValue placeholder="Statut" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="inactive">Inactifs</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-8 text-center text-gray-500">Chargement…</p>
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-gray-500">Aucun vendeur. Ajoutez votre premier vendeur avec le bouton ci-dessus.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b bg-gray-50 text-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2">Code</th>
                    <th className="text-left px-3 py-2">Nom</th>
                    <th className="text-left px-3 py-2">Email</th>
                    <th className="text-left px-3 py-2">Téléphone</th>
                    <th className="text-right px-3 py-2">Commission</th>
                    <th className="text-left px-3 py-2">Statut</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map(s => (
                    <tr key={s.id} className="hover:bg-gray-50" data-testid={`seller-row-${s.id}`}>
                      <td className="px-3 py-2 font-mono font-semibold text-gray-900">{s.seller_code}</td>
                      <td className="px-3 py-2">{s.first_name} {s.last_name}</td>
                      <td className="px-3 py-2 text-gray-600">{s.email || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">{s.phone || '-'}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {s.commission_amount_chf ? fmtChf(s.commission_amount_chf) : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-3 py-2">
                        {s.status === 'active' ? (
                          <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">Actif</span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">Inactif</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right space-x-1">
                        <Button size="sm" variant="outline" onClick={() => openCommissions(s)} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid={`commissions-${s.id}`}>
                          <Wallet size={14} className="mr-1" /> Commissions
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleExport(s)} data-testid={`export-seller-${s.id}`}>
                          <Download size={14} className="mr-1" /> CSV
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleOpenEdit(s)} data-testid={`edit-seller-${s.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(s)} data-testid={`delete-seller-${s.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-seller">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier le vendeur' : 'Nouveau vendeur'}</DialogTitle>
            <DialogDescription>
              Code à 6 chiffres généré automatiquement si vous laissez le champ vide.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <Label>Prénom *</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="input-seller-firstname" />
            </div>
            <div className="col-span-1">
              <Label>Nom *</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="input-seller-lastname" />
            </div>
            <div className="col-span-2">
              <Label>Adresse</Label>
              <Input value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="col-span-1">
              <Label>Téléphone</Label>
              <Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="col-span-1">
              <Label>Email</Label>
              <Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-seller-email" />
            </div>
            <div className="col-span-1">
              <Label>Code vendeur (6 chiffres, auto si vide)</Label>
              <Input
                value={form.seller_code || ''}
                onChange={(e) => setForm({ ...form, seller_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                placeholder="ex. 482190"
                className="font-mono"
                data-testid="input-seller-code"
              />
            </div>
            <div className="col-span-1">
              <Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="inactive">Inactif</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Section Commission */}
            <div className="col-span-2 border-t pt-3 mt-1">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5 mb-2">
                <Wallet size={14} className="text-indigo-600" /> Commission
              </h3>
            </div>
            <div className="col-span-1">
              <Label>Montant fixe par contrat (CHF) *</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={form.commission_amount_chf}
                onChange={(e) => setForm({ ...form, commission_amount_chf: Number(e.target.value) })}
                placeholder="ex. 10"
                data-testid="input-seller-commission"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Versé au 1er paiement de chaque contrat. Modifier garde l'historique pour ne pas casser les anciens contrats.
              </p>
            </div>
            {editing && Array.isArray(editing.commission_history) && editing.commission_history.length > 0 && (
              <div className="col-span-1">
                <Label className="text-xs text-gray-500">Historique du barème</Label>
                <div className="text-xs space-y-1 mt-1 max-h-20 overflow-y-auto border rounded p-2 bg-gray-50">
                  {editing.commission_history.slice().reverse().map((h, i) => (
                    <div key={i} className="font-mono text-gray-700">
                      {fmtDate(h.effective_from)} → <strong>{fmtChf(h.amount_chf)}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section Coordonnées bancaires */}
            <div className="col-span-2 border-t pt-3 mt-1">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Coordonnées bancaires (optionnel — affichées sur le relevé PDF)</h3>
            </div>
            <div className="col-span-2">
              <Label>Titulaire du compte</Label>
              <Input value={form.bank_account_holder || ''} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })} data-testid="input-seller-holder" />
            </div>
            <div className="col-span-1">
              <Label>IBAN</Label>
              <Input value={form.iban || ''} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="CH..." className="font-mono" data-testid="input-seller-iban" />
            </div>
            <div className="col-span-1">
              <Label>BIC / SWIFT</Label>
              <Input value={form.bic || ''} onChange={(e) => setForm({ ...form, bic: e.target.value })} className="font-mono" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving || !form.first_name || !form.last_name} className="bg-green-600 hover:bg-green-700" data-testid="btn-save-seller">
              {saving ? 'Enregistrement…' : (editing ? 'Mettre à jour' : 'Créer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commissions panel */}
      <CommissionsModal
        open={commissionsOpen}
        onOpenChange={setCommissionsOpen}
        seller={commissionsSeller}
        token={token}
        toast={toast}
      />
    </div>
  );
};

const CommissionsModal = ({ open, onOpenChange, seller, token, toast }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total: 0, pending: 0, paid: 0 });
  const [loading, setLoading] = useState(false);
  const [paymentRef, setPaymentRef] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    if (!seller) return;
    setLoading(true);
    try {
      const url = new URL(`${API}/admin/sellers/${seller.id}/commissions`);
      url.searchParams.set('month', month);
      url.searchParams.set('year', year);
      const r = await fetch(url, { headers });
      const d = await r.json();
      setItems(d.commissions || []);
      setTotals({ total: d.total_chf, pending: d.pending_chf, paid: d.paid_chf });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && seller) fetchData();
    // eslint-disable-next-line
  }, [open, seller, month, year]);

  const generatePdf = async () => {
    try {
      const url = `${API}/admin/sellers/${seller.id}/commissions/statement?month=${month}&year=${year}`;
      const r = await fetch(url, { method: 'POST', headers });
      if (!r.ok) throw new Error('Génération échouée');
      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = `releve-${seller.seller_code}-${String(month).padStart(2,'0')}-${year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
      toast({ title: 'Relevé généré', description: `PDF téléchargé pour ${MONTHS_FR[month-1]} ${year}.` });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.message });
    }
  };

  const markPaid = async (commissionId) => {
    try {
      const r = await fetch(`${API}/admin/commissions/${commissionId}/mark-paid`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_reference: paymentRef || null }),
      });
      if (!r.ok) throw new Error('Erreur');
      toast({ title: 'Commission marquée payée' });
      setConfirmingId(null);
      setPaymentRef('');
      fetchData();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.message });
    }
  };

  const markPending = async (commissionId) => {
    try {
      const r = await fetch(`${API}/admin/commissions/${commissionId}/mark-pending`, { method: 'PATCH', headers });
      if (!r.ok) throw new Error('Erreur');
      toast({ title: 'Commission remise en attente' });
      fetchData();
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erreur', description: err.message });
    }
  };

  if (!seller) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dialog-commissions">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="text-indigo-600" size={20} />
            Commissions — {seller.first_name} {seller.last_name} ({seller.seller_code})
          </DialogTitle>
          <DialogDescription>
            Sélectionnez un mois pour visualiser et générer le relevé. Barème actuel : {fmtChf(seller.commission_amount_chf)} par contrat.
          </DialogDescription>
        </DialogHeader>

        {/* Selectors */}
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs">Mois</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-36" data-testid="select-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS_FR.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Année</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28" data-testid="select-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027, 2028].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={generatePdf} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="btn-generate-statement">
            <FileText size={14} className="mr-1" /> Générer le relevé PDF
          </Button>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-3 gap-3 mt-3">
          <KpiCell label="Total mois" value={fmtChf(totals.total)} color="text-gray-900" />
          <KpiCell label="En attente" value={fmtChf(totals.pending)} color="text-yellow-700" icon={Clock} />
          <KpiCell label="Payé" value={fmtChf(totals.paid)} color="text-green-700" icon={CheckCircle2} />
        </div>

        {/* List */}
        <div className="border rounded mt-3">
          {loading ? (
            <p className="p-6 text-center text-gray-500">Chargement…</p>
          ) : items.length === 0 ? (
            <p className="p-6 text-center text-gray-500">Aucune commission sur ce mois.</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700 text-xs">
                <tr>
                  <th className="text-left px-3 py-2">Date contrat</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Contrat</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-right px-3 py-2">Commission</th>
                  <th className="text-left px-3 py-2">Statut</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map(c => (
                  <tr key={c.id} data-testid={`commission-row-${c.id}`}>
                    <td className="px-3 py-2">{fmtDate(c.signed_at)}</td>
                    <td className="px-3 py-2">{c.customer_name}<br/><span className="text-xs text-gray-500">{c.customer_email}</span></td>
                    <td className="px-3 py-2">{c.contract_label}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.source === 'stripe' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-800'}`}>
                        {c.source === 'stripe' ? 'Stripe' : 'Facture'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{fmtChf(c.amount_chf)}</td>
                    <td className="px-3 py-2">
                      {c.status === 'paid' ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 inline-flex items-center gap-1">
                          <CheckCircle2 size={11} /> Payée
                          {c.payment_reference ? ` (${c.payment_reference})` : ''}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 inline-flex items-center gap-1">
                          <Clock size={11} /> En attente
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {c.status === 'pending' && confirmingId !== c.id && (
                        <Button size="sm" variant="outline" onClick={() => { setConfirmingId(c.id); setPaymentRef(''); }} className="text-green-700 border-green-300" data-testid={`mark-paid-${c.id}`}>
                          <CheckCircle2 size={13} className="mr-1" /> Marquer payée
                        </Button>
                      )}
                      {confirmingId === c.id && (
                        <div className="flex items-center gap-1">
                          <Input
                            value={paymentRef}
                            onChange={(e) => setPaymentRef(e.target.value)}
                            placeholder="Réf paiement"
                            className="h-7 text-xs w-32"
                            data-testid={`ref-paid-${c.id}`}
                          />
                          <Button size="sm" onClick={() => markPaid(c.id)} className="bg-green-600 hover:bg-green-700 h-7" data-testid={`confirm-paid-${c.id}`}>OK</Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmingId(null)} className="h-7"><X size={12}/></Button>
                        </div>
                      )}
                      {c.status === 'paid' && (
                        <Button size="sm" variant="ghost" onClick={() => markPending(c.id)} className="text-gray-500" data-testid={`mark-pending-${c.id}`}>
                          Annuler
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const KpiCell = ({ label, value, color, icon: Icon }) => (
  <div className="rounded border bg-white p-3">
    <p className="text-xs text-gray-500 flex items-center gap-1">{Icon && <Icon size={12}/>} {label}</p>
    <p className={`text-lg font-bold ${color || ''}`}>{value}</p>
  </div>
);

export default SellersPage;
